import Tooltip from "@/components/Tooltip";
import { AnchorGroup } from "@/components/anchors-navigation";
import FilterButton from "@/components/filtering/Button";
import configuration from "@/configuration";
import { useCurrentPath, useWindowSize } from "@/hooks";
import { Software } from "@/services/global-services/models";
import { PageType } from "@/services/shared-models";
import { Element } from "@/services/user-services/models";
import { useDataStore, useFilterStore } from "@/stores";
import { localeIncludes } from "@/utils/LocaleIncludes";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrianglePersonDigging } from "@fortawesome/sharp-light-svg-icons";
import { Combobox, ComboboxInput, ComboboxOption, ComboboxOptions } from "@headlessui/react";
import { ExclamationCircleIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import match from "autosuggest-highlight/match";
import parse from "autosuggest-highlight/parse";
import clsx from "clsx";
import { Fragment, memo, useDeferredValue, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, Link, useNavigate, useParams } from "react-router-dom";
import { getProcessedImage } from "./getProcessedImage";

const groups = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

// When true, only the classification system currently selected in the filters is considered for
// search/display; when false, every classification present on the element is included.
const ONLY_SHOW_ACTIVE_CLASSIFICATION = false;

const CLASSIFICATION_EXCLUDED_SUFFIX = " NLBE-SfB";

// Characters of context kept on either side of the first match when snippetting.
const SNIPPET_RADIUS = 30;
const SNIPPET_ELLIPSIS = "…";
// Caps how many separate match windows a snippet can show - a common term (e.g. "wall", "pipe")
// can occur dozens of times in a classification list, so this keeps the combobox scannable.
const SNIPPET_MAX_FRAGMENTS = 3;

interface SearchResult extends Element {
    score: number;
    highlight: string;
}

interface ClassificationMatch {
    id: string;
    name: string;
    values: string[];
}

// Some element data uses a placeholder like a literal "-" in place of "no value", which otherwise
// shows up as a stray ", - ," gap once joined into a single string.
const isMeaningfulValue = (value: string): boolean => {
    const trimmed = value.trim();
    return trimmed.length > 0 && trimmed !== "-";
};

const getDisplayableClassifications = (
    element: Element,
    classifications: Software[],
    activeClassification: string | null
): ClassificationMatch[] => {
    if (!element.ifcClassifications) return [];

    return Object.entries(element.ifcClassifications)
        .filter(([id]) => !ONLY_SHOW_ACTIVE_CLASSIFICATION || !activeClassification || id === activeClassification)
        .map(([id, values]) => ({
            id,
            name: classifications.find((x) => x.id === id)?.name || id,
            values: values
                .map(cleanText)
                .filter(
                    (value) =>
                        value !== "N/A" && !value.endsWith(CLASSIFICATION_EXCLUDED_SUFFIX) && isMeaningfulValue(value)
                ),
        }))
        .filter((entry) => entry.values.length > 0);
};

// Some keyword/classification values coming from BIMQ carry leftover markup or HTML entities
// (e.g. a literal "&nbsp;" string, or a stray "<br>") instead of plain text. Strip that out before
// matching/snippetting so it never surfaces in the combobox.
const cleanText = (text: string): string =>
    text
        .replace(/<[^>]+>/g, " ")
        .replace(/&nbsp;/gi, " ")
        .replace(/&amp;/gi, "&")
        .replace(/&lt;/gi, "<")
        .replace(/&gt;/gi, ">")
        .replace(/&quot;/gi, '"')
        .replace(/&#39;/gi, "'")
        .replace(/\s+/g, " ")
        .trim();

// Mirrors Algolia's Snippet widget for text that isn't coming from an Algolia query: instead of
// rendering the full (sometimes very long) keyword/classification list, keep only a window of
// context around each match so the combobox stays scannable. Returns null when the query doesn't
// actually match this text, so the caller can hide the block instead of showing an unrelated excerpt.
const getSnippet = (text: string, query: string, radius: number = SNIPPET_RADIUS): string | null => {
    if (!text) return null;

    const matches = match(text, query, { insideWords: true, findAllOccurrences: true }).sort((a, b) => a[0] - b[0]);
    if (matches.length === 0) return null;

    // Merge overlapping/adjacent match windows so nearby matches share one fragment instead of
    // producing duplicated or overlapping text.
    const windows: Array<[number, number]> = [];
    for (const [start, end] of matches) {
        const from = Math.max(0, start - radius);
        const to = Math.min(text.length, end + radius);
        const last = windows[windows.length - 1];
        if (last && from <= last[1]) last[1] = Math.max(last[1], to);
        else windows.push([from, to]);
    }

    const shown = windows.slice(0, SNIPPET_MAX_FRAGMENTS);
    const body = shown.map(([from, to]) => text.slice(from, to).trim()).join(` ${SNIPPET_ELLIPSIS} `);
    const prefix = shown[0][0] > 0 ? `${SNIPPET_ELLIPSIS} ` : "";
    const suffix = shown[shown.length - 1][1] < text.length ? ` ${SNIPPET_ELLIPSIS}` : "";

    return `${prefix}${body}${suffix}`;
};

const renderName = (name: string, highlight: string) => {
    const cleanName = cleanText(name);
    const matches = match(cleanName, highlight, { insideWords: true, findAllOccurrences: true });
    const parts = parse(cleanName, matches);

    return parts.map((part, index) => {
        return (
            <span className={clsx(["inline", part.highlight && "font-bold"])} key={index}>
                {part.text}
            </span>
        );
    });
};

const renderKeywords = (keywords: Array<string>, excludeValues: Set<string>, highlight: string) => {
    const filtered = keywords
        .map(cleanText)
        .filter(isMeaningfulValue)
        .filter((value) => !excludeValues.has(value.toLowerCase()));
    const snippet = getSnippet(filtered.join(", "), highlight);
    if (!snippet) return null;

    const matches = match(snippet, highlight, { insideWords: true, findAllOccurrences: true });
    const parts = parse(snippet, matches);

    return (
        <p className="text-sm text-gray-500 group-data-focus:text-gray-700">
            {parts.map((part, index) => (
                <span key={index} className={clsx(["inline whitespace-pre-wrap", part.highlight && "font-bold"])}>
                    {part.text}
                </span>
            ))}
        </p>
    );
};

const renderClassifications = (classificationMatches: Array<ClassificationMatch>, highlight: string) => {
    return classificationMatches.map((entry) => {
        const snippet = getSnippet(entry.values.join(", "), highlight);
        if (!snippet) return null;

        const matches = match(snippet, highlight, { insideWords: true, findAllOccurrences: true });
        const parts = parse(snippet, matches);

        return (
            <p key={entry.id} className="text-sm text-gray-500 group-data-focus:text-gray-700">
                <span className="font-medium">{entry.name}: </span>
                {parts.map((part, index) => (
                    <span key={index} className={clsx(["inline whitespace-pre-wrap", part.highlight && "font-bold"])}>
                        {part.text}
                    </span>
                ))}
            </p>
        );
    });
};

const Elements = () => {
    const elements = useDataStore((state) => state.elements);
    const pages = useDataStore((state) => state.pages);
    const classifications = useDataStore((state) => state.classifications);
    const activeVersion = useFilterStore((state) => state.activeVersion);
    const activeDisciplines = useFilterStore((state) => state.activeDisciplines);
    const activeUseCases = useFilterStore((state) => state.activeUseCases);
    const activeClassification = useFilterStore((state) => state.activeClassification);

    const [searchValue, setSearchValue] = useState<string>("");
    const deferredSearchValue = useDeferredValue(searchValue);
    const [searchResults, setSearchResults] = useState<Array<SearchResult>>([]);

    const [width] = useWindowSize();
    const [left, setLeft] = useState(0);
    const [data, setData] = useState<Element[]>([]);
    const [firstLetterFilter, setFirstLetterFilter] = useState<string>("");

    const currentPath = useCurrentPath();
    const navigate = useNavigate();
    const params = useParams();

    const elementListPage = useMemo(() => pages.find((x) => x.pageType === PageType.Elements), [pages]);

    const { t } = useTranslation("elements");

    useLayoutEffect(() => {
        if (width === 2560) {
            setLeft(0);
        } else {
            const change = 2560 - width;
            setLeft(change * -0.5);
        }
    }, [width]);

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }, []);

    useEffect(() => {
        console.log("activeUseCases", activeUseCases);
        setData(
            elements[activeVersion!]?.filter((element) => {
                const attachedToDiscipline = element.disciplines.some((discipline) =>
                    activeDisciplines.includes(discipline)
                );

                if (!attachedToDiscipline) return false;
                if (!element.useCases) return true;

                return element.useCases.some((useCase) => activeUseCases.includes(useCase));
            })
        );
    }, [elements, activeVersion, activeDisciplines, activeUseCases]);

    useEffect(() => {
        const results: Array<SearchResult> = [];

        if (!activeVersion || !elements[activeVersion]) return setSearchResults([]);
        if (!deferredSearchValue) return setSearchResults(results);

        for (let i = 0; i < elements[activeVersion!].length; i++) {
            const element = elements[activeVersion!][i];

            if (
                localeIncludes(element.name, deferredSearchValue, {
                    usage: "search",
                    sensitivity: "base",
                    locales: "en",
                })
            ) {
                let score = 250;
                if (element.name.localeCompare(deferredSearchValue, "en", { sensitivity: "base" }) === 0) score = 500;

                results.push({ ...element, score, highlight: deferredSearchValue });
            }

            for (let j = 0; j < element.keywords.length; j++) {
                const keyword = element.keywords[j];

                if (
                    localeIncludes(keyword, deferredSearchValue, {
                        usage: "search",
                        sensitivity: "base",
                        locales: "en",
                    })
                ) {
                    let score = 1;
                    if (keyword.localeCompare(deferredSearchValue, "en", { sensitivity: "base" }) === 0) score = 5;

                    const existingResult = results.find((x) => x.name === element.name);
                    if (!existingResult) results.push({ ...element, score, highlight: deferredSearchValue });
                    else existingResult.score += score;
                }
            }

            const classificationMatches = getDisplayableClassifications(
                element,
                classifications[activeVersion!] ?? [],
                activeClassification
            );

            for (const classificationMatch of classificationMatches) {
                for (const value of classificationMatch.values) {
                    if (
                        localeIncludes(value, deferredSearchValue, {
                            usage: "search",
                            sensitivity: "base",
                            locales: "en",
                        })
                    ) {
                        let score = 1;
                        if (value.localeCompare(deferredSearchValue, "en", { sensitivity: "base" }) === 0) score = 5;

                        const existingResult = results.find((x) => x.name === element.name);
                        if (!existingResult) results.push({ ...element, score, highlight: deferredSearchValue });
                        else existingResult.score += score;
                    }
                }
            }
        }

        setSearchResults(results);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deferredSearchValue]);

    const renderItems = (letter: string) => {
        if (data.length === 0)
            return [0, 1, 2, 3].map((_, index) => (
                <div
                    key={index}
                    className="flex flex-col rounded-lg bg-primary-100 text-primary-500 text-base font-medium"
                >
                    <div className="flex items-center justify-center w-full aspect-3/2 bg-gray-300 rounded-t-lg  animate-pulse">
                        <svg
                            className="w-10 h-10 text-gray-200 dark:text-gray-600"
                            aria-hidden="true"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="currentColor"
                            viewBox="0 0 20 18"
                        >
                            <path d="M18 0H2a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2Zm-5.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Zm4.376 10.481A1 1 0 0 1 16 15H4a1 1 0 0 1-.895-1.447l3.5-7A1 1 0 0 1 7.468 6a.965.965 0 0 1 .9.5l2.775 4.757 1.546-1.887a1 1 0 0 1 1.618.1l2.541 4a1 1 0 0 1 .028 1.011Z" />
                        </svg>
                    </div>
                    <span className="flex flex-1 px-7 py-5 items-center place-content-center text-center">
                        <div className="h-2.5 bg-gray-200 rounded-full w-48"></div>
                    </span>
                </div>
            ));

        return data
            .filter((item) => item.name.toUpperCase().startsWith(letter.toUpperCase()))
            .map((item) => <ElementDisplay key={item.slug} element={item} />);
    };

    return (
        <>
            <div
                className={`absolute w-full h-full bg-primary-100 overflow-hidden`}
                style={{
                    left: `${left}px`,
                    width: 2015,
                    clipPath: "polygon(200px 0, 2015px 0, 838px 450px)",
                }}
            />

            <div className="container mx-auto 2xl:px-[108px]">
                <section id="hero" className="relative pt-[60px] mb-1">
                    <div className="flex flex-col">
                        <h1 className="flex content-center gap-3 items-center">
                            {t("list.title", { ns: "elements" })}
                        </h1>
                        <div className="text-lg/[32px] font-medium mt-2 max-w-[920px]">
                            {t("list-description", { ns: "elements" })}
                        </div>

                        <div className="flex flex-col gap-3 mt-20 w-fit self-center">
                            <div className="relative">
                                <Combobox
                                    onChange={(item: SearchResult | null) => {
                                        if (item) {
                                            const url = generatePath(currentPath, {
                                                ...params,
                                                "*": `${elementListPage!.slug}/${item.slug}`,
                                            }).replace(":/", "");
                                            navigate(url);
                                        }
                                    }}
                                >
                                    <form noValidate autoComplete="off" className="flex">
                                        <div
                                            className={clsx([
                                                "relative flex flex-auto rounded-l-full bg-white border border-r-0 -outline-offset-1 border-[#E6E8EC] h-12",
                                                searchValue !== "" && "rounded-tl-3xl rounded-bl-none",
                                            ])}
                                        >
                                            <ComboboxInput
                                                autoFocus
                                                className="col-start-1 row-start-1 h-12 w-full pr-4 pl-6 text-base text-gray-900 outline-hidden placeholder:text-gray-400"
                                                placeholder={t("list.search-placeholder", { ns: "elements" })}
                                                onChange={(event) => setSearchValue(event.target.value)}
                                                onBlur={() => setSearchValue("")}
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            className={clsx([
                                                "flex shrink-0 items-center gap-x-1.5 rounded-r-full bg-primary-500 pl-4 pr-5 py-2 text-white hover:bg-primary-600 cursor-pointer font-medium",
                                                searchValue !== "" && "rounded-tr-3xl rounded-br-none",
                                            ])}
                                        >
                                            <MagnifyingGlassIcon
                                                aria-hidden="true"
                                                className="pointer-events-none col-start-1 row-start-1 size-6 self-center justify-self-end"
                                            />
                                            {t("list.search", { ns: "elements" })}
                                        </button>
                                    </form>

                                    {searchResults.length > 0 && (
                                        <ComboboxOptions
                                            static
                                            className="absolute w-full z-100 max-h-96 transform-gpu scroll-py-3 overflow-y-auto p-3 bg-white border border-t-0 border-[#E6E8EC] rounded-b-3xl"
                                        >
                                            {searchResults
                                                .sort((a, b) => b.score - a.score)
                                                .map((item) => {
                                                    const classificationMatches = getDisplayableClassifications(
                                                        item,
                                                        classifications[activeVersion!] ?? [],
                                                        activeClassification
                                                    );

                                                    const classificationValues = new Set(
                                                        classificationMatches
                                                            .flatMap((entry) => entry.values)
                                                            .flatMap((value) => [
                                                                value,
                                                                ...value.split(",").map((part) => part.trim()),
                                                            ])
                                                            .map((value) => value.toLowerCase())
                                                    );

                                                    return (
                                                        <ComboboxOption
                                                            key={item.slug}
                                                            value={item}
                                                            className="group flex cursor-default rounded-xl p-3 select-none data-focus:bg-gray-100 data-focus:outline-hidden"
                                                        >
                                                            <div className="ml-4 flex-auto">
                                                                <p className="text-sm font-medium text-gray-700 group-data-focus:text-gray-900">
                                                                    {renderName(item.name, item.highlight)}
                                                                </p>
                                                                {renderKeywords(
                                                                    item.keywords,
                                                                    classificationValues,
                                                                    item.highlight
                                                                )}
                                                                {renderClassifications(
                                                                    classificationMatches,
                                                                    item.highlight
                                                                )}
                                                            </div>
                                                        </ComboboxOption>
                                                    );
                                                })}
                                        </ComboboxOptions>
                                    )}

                                    {searchValue !== "" && searchResults.length === 0 && (
                                        <div className="absolute bg-white w-full z-100 px-6 py-14 text-center text-sm sm:px-14">
                                            <ExclamationCircleIcon
                                                type="outline"
                                                name="exclamation-circle"
                                                className="mx-auto size-6 text-gray-400"
                                            />
                                            <p className="mt-4 font-semibold text-gray-900">
                                                {t("list.search-empty", { ns: "elements" })}
                                            </p>
                                            <p className="mt-2 text-gray-500">
                                                {t("list.search-empty-description", { ns: "elements" })}
                                            </p>
                                        </div>
                                    )}
                                </Combobox>
                            </div>

                            <div className="bg-white border border-[#E6E8EC] rounded-lg p-2">
                                <span className="isolate inline-flex rounded-md">
                                    {groups.map((letter) => (
                                        <button
                                            key={letter}
                                            onClick={() => setFirstLetterFilter(letter)}
                                            disabled={
                                                !data?.some((item) =>
                                                    item.name.toUpperCase().startsWith(letter.toUpperCase())
                                                )
                                            }
                                            className={clsx([
                                                "cursor-pointer inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium first:rounded-l-md last:rounded-r-md text-primary-500 bg-primary-100 hover:bg-primary-600 hover:text-white",
                                                firstLetterFilter === letter && "bg-primary-500 text-white",
                                                "disabled:opacity-30 disabled:pointer-events-none",
                                            ])}
                                        >
                                            {letter}
                                        </button>
                                    ))}
                                    <button
                                        onClick={() => setFirstLetterFilter("")}
                                        className={clsx([
                                            "cursor-pointer inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium first:rounded-l-md last:rounded-r-md text-primary-500 bg-primary-100 hover:bg-primary-600 hover:text-white",
                                            firstLetterFilter === "" && "bg-primary-500 text-white",
                                        ])}
                                    >
                                        {t("list.filter-all", { ns: "elements" })}
                                    </button>
                                </span>
                            </div>
                        </div>
                    </div>
                </section>

                <main className="pb-40">
                    <div className="relative grid grid-cols-[auto_64px_1fr] gap-13 gap-y-20 mt-23">
                        <AnchorGroup>
                            <FilterButton />
                        </AnchorGroup>

                        {groups.map((letter) => {
                            const hasElements = data?.some((item) =>
                                item.name.toUpperCase().startsWith(letter.toUpperCase())
                            );

                            if (!hasElements) return null;
                            if (firstLetterFilter && firstLetterFilter !== letter) return null;

                            return (
                                <Fragment key={letter}>
                                    <div className="relative w-full col-start-2">
                                        <span className="absolute -right-[0.5px] rounded-full bg-primary-200 text-primary-500 w-16 h-16 text-[35px] inline-flex items-center justify-center font-bold z-1 select-none">
                                            {letter}
                                        </span>
                                    </div>
                                    <div className="grid grid-cols-3 xl:grid-cols-4 gap-10">{renderItems(letter)}</div>
                                </Fragment>
                            );
                        })}
                    </div>
                </main>

                <hr className="h-px bg-secondary-300 border-0" />
            </div>
        </>
    );
};

export default Elements;

const ElementDisplay = memo(({ element }: { element: Element }) => {
    const [imgUrl, setImgUrl] = useState<string | null>(null);
    const { t } = useTranslation(["elements"]);

    useEffect(() => {
        let cancelled = false;

        if (element.image) {
            const path = `${configuration.assets}${element.image}`;

            setImgUrl(path);
            getProcessedImage(path).then((url) => {
                if (!cancelled) setImgUrl(url);
            });
        } else {
            setImgUrl(null);
        }

        return () => {
            cancelled = true;
        };
    }, [element.image]);

    return (
        <Link
            to={element.slug}
            className="flex flex-col rounded-lg bg-primary-100 text-primary-500 text-base font-medium"
        >
            {!imgUrl && <div className="flex items-center justify-center w-full aspect-3/2 bg-gray-300 rounded-t-lg" />}

            {imgUrl && (
                <div
                    className="relative flex items-center justify-center w-full aspect-3/2 bg-gray-300 rounded-t-lg bg-cover bg-center"
                    style={{ backgroundImage: `url(${imgUrl})` }}
                ></div>
            )}

            <div className="flex flex-1 px-3 py-4 items-center place-content-center text-center">
                <span className="flex flex-1 text-left">{element.name}</span>
                {element.isCompleted === false && (
                    <Tooltip text={t("incomplete.tooltip", { ns: "elements" })}>
                        <FontAwesomeIcon icon={faTrianglePersonDigging} size="xl" className="ml-auto" />
                    </Tooltip>
                )}
            </div>
        </Link>
    );
});
